import React from 'react';
import { Box } from '@heycater/design-system';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CateringCategoryValue } from 'qualification/schema/questions/cateringCategoriesQuestion';
import BackgroundImage from 'shared/components/BackgroundImage';
import { BENEFITS } from 'shared/constants';
import { createSizes } from 'shared/helpers/createSizes';
import CitySelector from 'static_pages/components/CitySelector';
import HeroRatings from 'static_pages/marketing_pages/SEAPage/HeroRatings';

interface Props {
  title?: string;
  keywords?: CateringCategoryValue[];
  city?: string;
  rating: number;
}

function Hero({ title, city, keywords, rating }: Props) {
  const { t } = useTranslation('static_pages');

  return (
    <React.Fragment>
      <HeroSection as="section">
        <BackgroundImage
          priority
          layout="fill"
          alt=""
          objectFit="cover"
          objectPosition="50% 70%"
          quality={90}
          src="/artifacts/images/homepage/hero-alternate/bg.jpg"
          sizes={createSizes({ xs: '100vw' })}
        />
        <Content>
          <ColumnLeft>
            <HeroTitle>{title}</HeroTitle>
            <Box pr={{ xs: 0, md: 4 }} pb={{ xs: 4, md: 0.5 }}>
              <CitySelector
                cityKeyContentful={city}
                cateringCategories={keywords}
                buttonLabelKey={'marketingPages.hero.generalCTA'}
              />
            </Box>
          </ColumnLeft>
          <ColumnRight>
            <LogoWrapper>
              <HeroRatings rating={rating} />

              <Image
                alt="ClimatePartner Logo"
                src="/artifacts/images/homepage/hero-alternate/climatepartner_2x.png"
                objectFit="contain"
                width={190}
                height={29}
              />
            </LogoWrapper>
            <Checklist>
              <ChecklistItem>{t('common:benefits.quickAndFree')}</ChecklistItem>
              <ChecklistItem>
                {t('common:benefits.satisfaction', {
                  percent: BENEFITS.CUSTOMER_SATISFACTION_PERCENT,
                })}
              </ChecklistItem>
              <ChecklistItem>
                {t('common:benefits.personalService')}
              </ChecklistItem>
              <ChecklistItem>
                {t('common:benefits.catererCount', {
                  count: BENEFITS.CATERER_COUNT,
                })}
              </ChecklistItem>
            </Checklist>
          </ColumnRight>
        </Content>
      </HeroSection>
    </React.Fragment>
  );
}

const HeroSection = styled.div`
  position: relative;
  padding: ${(props) => props.theme.spacing(13, 2)};

  ${(props) => props.theme.breakpoints.up('md')} {
    align-items: center;
    background-size: cover;
    display: flex;
    justify-content: center;
    padding: ${(props) => props.theme.spacing(20, 4)};
  }
`;

const Content = styled.div`
  background-color: ${(props) => props.theme.palette.secondary['450']};
  color: white;
  align-items: center;
  flex-direction: column;
  text-align: center;
  display: flex;
  padding: ${(props) => props.theme.spacing(4.2, 3)};
  width: 100%;
  border-radius: ${(props) => props.theme.spacing(1)}px;
  max-width: ${({ theme }) => theme.breakpoints.width('sm')}px;
  margin: 0 auto;

  ${(props) => props.theme.breakpoints.up('md')} {
    align-items: stretch;
    flex-direction: row;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(8)}px;
    padding: ${(props) => props.theme.spacing(4.5, 6)};
    max-width: 1060px;
    text-align: left;
  }
`;

const ColumnLeft = styled.div`
  max-width: 520px;
  text-align: center;
  margin-bottom: 0px;

  ${(props) => props.theme.breakpoints.up('sm')} {
  }
  ${(props) => props.theme.breakpoints.up('md')} {
    margin-bottom: ${(props) => props.theme.spacing(5)}px;
    flex: 5;
    text-align: start;
    display: flex;
    margin-bottom: ${(props) => props.theme.spacing(0)}px;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const ColumnRight = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    flex: 3;
    max-width: 400px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 26px;
  line-height: 160%;
  margin: 0;
  margin-bottom: ${({ theme }) => theme.spacing(4.5)}px;

  ${(props) => props.theme.breakpoints.up('md')} {
    font-size: 42px;
    margin-bottom: 0;
    line-height: 160%;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)}px;
  margin: ${(props) => props.theme.spacing(0, 0, 4)};

  ${(props) => props.theme.breakpoints.up('sm')} {
    flex-direction: row;
    gap: ${(props) => props.theme.spacing(2)}px;
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    justify-content: start;
    margin: ${(props) => props.theme.spacing(0, 0, 3)};
  }
`;

const Checklist = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)}px;
`;

const ChecklistItem = styled.li`
  background: url('/artifacts/images/homepage/hero-alternate/checkmark.svg')
    no-repeat left 30%;
  font-size: 14px;
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  padding: ${(props) => props.theme.spacing(0, 0, 0, 4)};

  ${(props) => props.theme.breakpoints.up('md')} {
    font-size: 17px;
  }
`;

export default Hero;
